import { useEffect, useRef, useState } from 'react';

const ACTIVE_STATUS_OPTIONS = [
  { id: '', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'dispatched', label: 'Dispatched' },
  { id: 'completed', label: 'Completed' },
];

const TYPE_OPTIONS = [
  { id: '', label: 'All' },
  { id: 'retail', label: 'Retail' },
  { id: 'wholesale', label: 'Wholesale' },
];

const STAT_CHIPS = [
  { key: 'total', label: 'Total', color: 'text-blue-600', getValue: (stats, getCount) => getCount(stats.totalOrders) },
  { key: 'today', label: 'Today', color: 'text-green-600', getValue: (stats, getCount) => getCount(stats.todayOrders) },
  { key: 'pending', label: 'Pending', color: 'text-yellow-600', getValue: (stats, getCount) => getCount(stats.statusBreakdown?.find((s) => s._id === 'pending')?.count) },
  { key: 'dispatched', label: 'Dispatched', color: 'text-indigo-600', getValue: (stats, getCount) => getCount(stats.statusBreakdown?.find((s) => s._id === 'dispatched')?.count) },
  { key: 'completed', label: 'Completed', color: 'text-emerald-600', getValue: (stats, getCount) => getCount(stats.statusBreakdown?.find((s) => s._id === 'completed')?.count) },
];

const SearchIcon = () => (
  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg
    className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const OrdersToolbar = ({ stats = {}, filters, onFilterChange, onClearFilters, safeRender, orderTab = 'active' }) => {
  const searchRef = useRef(null);
  const [dateExpanded, setDateExpanded] = useState(Boolean(filters.fromDate || filters.toDate));
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    if (filters.fromDate || filters.toDate) {
      setDateExpanded(true);
    }
  }, [filters.fromDate, filters.toDate]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
      const tag = event.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || event.target?.isContentEditable) {
        return;
      }
      event.preventDefault();
      searchRef.current?.focus();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const getCount = (value) => safeRender(value, 0);

  const activeFilterCount =
    (filters.search?.trim() ? 1 : 0) +
    (filters.status ? 1 : 0) +
    (filters.type ? 1 : 0) +
    (filters.fromDate ? 1 : 0) +
    (filters.toDate ? 1 : 0) +
    (filters.sort && filters.sort !== 'newest' ? 1 : 0);

  const dateFilterActive = Boolean(filters.fromDate || filters.toDate);

  const renderChipRow = (label, options, field, currentValue, compact = false) => (
    <div className={`flex items-center gap-2 min-w-0 ${compact ? 'mb-2.5' : ''}`}>
      <span className="text-xs text-gray-500 shrink-0">{label}:</span>
      <div className={`filter-scroll filter-scroll--hint min-w-0 flex-1 ${compact ? 'filter-scroll--compact' : ''}`}>
        {options.map((option) => (
          <button
            key={option.id || `all-${field}`}
            type="button"
            onClick={() => onFilterChange(field, option.id)}
            className={`filter-chip ${compact ? 'filter-chip--compact' : ''} ${currentValue === option.id ? 'filter-chip-active' : ''}`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );

  const renderFilterGroup = (label, options, field, currentValue) => (
    <div className="orders-filter-group">
      <span className="orders-filter-label">{label}:</span>
      <div className="filter-scroll filter-scroll--hint orders-filter-pills">
        {options.map((option) => (
          <button
            key={option.id || `all-${field}`}
            type="button"
            onClick={() => onFilterChange(field, option.id)}
            className={`filter-chip ${currentValue === option.id ? 'filter-chip-active' : ''}`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );

  const renderStatsRow = (desktopInline = false) => (
    <div className={`filter-scroll filter-scroll--hint ${desktopInline ? 'orders-toolbar-stats' : 'pb-2 mb-2.5 border-b border-gray-100'}`}>
      {STAT_CHIPS.map((chip) => (
        <div key={chip.key} className="stat-chip">
          <span className={`stat-chip-value ${chip.color}`}>
            {chip.getValue(stats, getCount)}
          </span>
          <span className="stat-chip-label">{chip.label}</span>
        </div>
      ))}
    </div>
  );

  const renderDesktopDateRange = () => (
    <div className="orders-filter-date">
      <button
        type="button"
        onClick={() => setDateExpanded((prev) => !prev)}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap"
      >
        <ChevronIcon open={dateExpanded} />
        Date
        {dateFilterActive && (
          <span className="inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold">
            {(filters.fromDate ? 1 : 0) + (filters.toDate ? 1 : 0)}
          </span>
        )}
      </button>
      {dateExpanded && (
        <div className="orders-filter-date-inputs">
          <input
            type="date"
            value={filters.fromDate}
            onChange={(e) => onFilterChange('fromDate', e.target.value)}
            className="toolbar-select orders-filter-date-input"
            aria-label="From date"
          />
          <span className="text-xs text-gray-400">–</span>
          <input
            type="date"
            value={filters.toDate}
            onChange={(e) => onFilterChange('toDate', e.target.value)}
            className="toolbar-select orders-filter-date-input"
            aria-label="To date"
          />
        </div>
      )}
    </div>
  );

  const renderDateRange = () => (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setDateExpanded((prev) => !prev)}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors min-h-11 md:min-h-0"
      >
        <ChevronIcon open={dateExpanded} />
        Date range
        {dateFilterActive && (
          <span className="inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold">
            {(filters.fromDate ? 1 : 0) + (filters.toDate ? 1 : 0)}
          </span>
        )}
      </button>
      {dateExpanded && (
        <div className="mt-2 flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-xs text-gray-500 shrink-0">From</span>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => onFilterChange('fromDate', e.target.value)}
              className="toolbar-select flex-1 min-w-0"
              aria-label="From date"
            />
          </div>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-xs text-gray-500 shrink-0">To</span>
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => onFilterChange('toDate', e.target.value)}
              className="toolbar-select flex-1 min-w-0"
              aria-label="To date"
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderExtendedFilters = () => (
    <>
      {renderStatsRow()}
      {orderTab === 'active' && (
        <div className="pt-2.5 border-t border-gray-100">
          {renderChipRow('Status', ACTIVE_STATUS_OPTIONS, 'status', filters.status)}
        </div>
      )}
      <div className="mt-2">
        {renderChipRow('Type', TYPE_OPTIONS, 'type', filters.type)}
      </div>
      {renderDateRange()}
      {activeFilterCount > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between gap-2">
          <span className="text-xs text-gray-500">
            {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
          </span>
          <button
            type="button"
            onClick={onClearFilters}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors min-h-11 md:min-h-0 px-2"
          >
            Clear all
          </button>
        </div>
      )}
    </>
  );

  return (
    <div className="compact-toolbar">
      {/* Mobile: search + filters button + sort */}
      <div className="flex flex-col gap-2 md:hidden">
        <div className="relative flex-1 min-w-0">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
            <SearchIcon />
          </span>
          <input
            ref={searchRef}
            type="text"
            placeholder="Search order #, name, phone…"
            value={filters.search}
            onChange={(e) => onFilterChange('search', e.target.value)}
            className="toolbar-input"
            aria-label="Search orders"
          />
          {filters.search && (
            <button
              type="button"
              onClick={() => onFilterChange('search', '')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none min-w-11 min-h-11 flex items-center justify-center rounded"
              aria-label="Clear search"
            />
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setMobileFiltersOpen(true)}
            className="toolbar-btn-primary flex-1 min-h-11"
          >
            Filters
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-indigo-600 text-white text-[10px] font-semibold">
                {activeFilterCount}
              </span>
            )}
          </button>
          <select
            id="orders-sort-mobile"
            value={filters.sort}
            onChange={(e) => onFilterChange('sort', e.target.value)}
            className="toolbar-select shrink-0 min-h-11"
            aria-label="Sort by date"
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
          </select>
        </div>
      </div>

      {mobileFiltersOpen && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/40 md:hidden"
            onClick={() => setMobileFiltersOpen(false)}
            aria-hidden="true"
          />
          <div className="orders-filter-sheet md:hidden" role="dialog" aria-modal="true" aria-label="Order filters">
            <div className="orders-filter-sheet-header">
              <h3 className="text-base font-semibold text-gray-900">Filters</h3>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(false)}
                className="text-sm font-medium text-indigo-600 min-h-11 px-2"
              >
                Done
              </button>
            </div>
            {renderExtendedFilters()}
          </div>
        </>
      )}

      {/* Desktop: compact toolbar */}
      <div className="hidden md:block">
        <div className="orders-toolbar-top">
          {renderStatsRow(true)}
          <div className="orders-toolbar-controls">
            <div className="relative flex-1 min-w-0 max-w-md">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                <SearchIcon />
              </span>
              <input
                ref={searchRef}
                type="text"
                placeholder="Search order #, name, phone… (press /)"
                value={filters.search}
                onChange={(e) => onFilterChange('search', e.target.value)}
                className="toolbar-input"
                aria-label="Search orders"
              />
              {filters.search && (
                <button
                  type="button"
                  onClick={() => onFilterChange('search', '')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none min-w-11 min-h-11 flex items-center justify-center rounded"
                  aria-label="Clear search"
                />
              )}
            </div>
            <label className="sr-only" htmlFor="orders-sort">Sort by date</label>
            <select
              id="orders-sort"
              value={filters.sort}
              onChange={(e) => onFilterChange('sort', e.target.value)}
              className="toolbar-select shrink-0"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select>
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={onClearFilters}
                className="text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors whitespace-nowrap shrink-0 px-1"
              >
                Clear all
              </button>
            )}
          </div>
        </div>

        <div className="orders-filter-row">
          {orderTab === 'active' && (
            <>
              {renderFilterGroup('Status', ACTIVE_STATUS_OPTIONS, 'status', filters.status)}
              <div className="orders-filter-divider" aria-hidden="true" />
            </>
          )}
          {renderFilterGroup('Type', TYPE_OPTIONS, 'type', filters.type)}
          {renderDesktopDateRange()}
        </div>
      </div>
    </div>
  );
};

export default OrdersToolbar;
